import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableContainer,
  Button,
  Stack,

} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "store";
import { useParams } from "react-router-dom";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { IoIosSearch } from "react-icons/io";
import { useGetAllBOQQuery, useEditBoqListMutation } from "store/slices/apis/keyBOQSlice";
import { MdOutlineModeEdit } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { height, width } from "@mui/system";
import {ReactComponent as CloseIcon} from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"
import {ReactComponent as EditIcon} from "assets/PwC_Funct_Icons_Edit_Outline_Black_RGB.svg"

const StyledTableCell = (props) => (
  <TableCell
    {...props}
    sx={{
      whiteSpace: "nowrap",
      border: "1px solid #ccc",
      textAlign: "center",
      ...props.sx,
    }}
  />
);

const AllBOQ = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { projectId } = useParams();
  const [editRowIndex, setEditRowIndex] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [originalData, setOriginalData] = useState({});

  const [editBoqList] = useEditBoqListMutation();
  const { data: allBOQ, refetch } = useGetAllBOQQuery({
    projectId: projectId,
    page: pageNumber,
    pageSize: 10,
    search: searchQuery,
  });

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [searchQuery]);

  useEffect(() => {
    if (allBOQ?.data?.items) {
      const tempData = allBOQ.data.items.map((item) => ({ ...item }));
      setTableData(tempData);
    }
  }, [allBOQ]);

  const handleToggleEditClick = (index) => {
    if (!editRowIndex.includes(index)) {

      setOriginalData(prev => ({
        ...prev,
        [index]: { ...tableData[index] }
      }));
      setEditRowIndex(prev => [...prev, index]);
    } else {

      const updatedData = [...tableData];
      if (originalData[index]) {
        updatedData[index] = { ...originalData[index] };
      }
      setTableData(updatedData);
      setEditRowIndex(prev => prev.filter(i => i !== index));


      setOriginalData(prev => {
        const newData = { ...prev };
        delete newData[index];
        return newData;
      });
    }
  };

  const isRowEditable = (index) => editRowIndex.includes(index);

  const handleChange = (index, field, value) => {
    const updated = [...tableData];
    updated[index][field] = value;

    if (field === 'rate') {
      const rate = parseFloat(value) || 0;
      const quantity = parseFloat(updated[index]['quantity']) || 0;
      updated[index]['totalCost'] = (rate * quantity).toFixed(2);
    }

    setTableData(updated);
  };

  const clearSearch = () => setSearchQuery("");

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const filteredData = tableData.filter((row) =>
    (row.subCategory1 || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openNotification = (message, type = 'success') => {
    dispatch(openSnackbar({
      open: true,
      message,
      variant: 'alert',
      alert: {
        color: type,
      },
      close: false,
    }));

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleSubmitConfirm = async () => {
    try {
      const payload = tableData.filter((_, index) => editRowIndex.includes(index));
      const response = await editBoqList(payload).unwrap();

      if (response) {
        const updatedData = tableData.map((row, index) => {
          if (editRowIndex.includes(index)) {
            const updatedRow = payload.find(p => p.id === row.id);
            return { ...row, ...updatedRow };
          }
          return row;
        });

        setTableData(updatedData);
        openNotification("Data updated successfully", "success");
      }

      setEditRowIndex([]);
      setOriginalData({});
      setConfirmationModal(false);
    } catch (error) {
      openNotification(error?.data?.message || "Something went wrong", "error");
      setConfirmationModal(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>BOQ List</Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: 'start', sm: 'center' },
          justifyContent: "space-between",
          flexDirection: { xs: 'column', sm: 'row' }
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            // flexGrow: 2,
            alignItems: { xs: "stretch", sm: "center" }, // Full width on mobile
            width: '100%',
            justifyContent: 'space-between',
            "& .MuiStack-root": {
              paddingTop: "15px",
            },
          }}
        >
          <TextField
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by Sub Category"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoIosSearch />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <IconButton onClick={clearSearch} edge="end">
                  <ClearIcon style={{ fontSize: "16px" }} />
                </IconButton>
              ),
            }}
            sx={{ width: { xs: '100%', sm: '400px' }, backgroundColor: "#fff" }}
            size="small"
          />

          <Button
            variant="contained"
            onClick={() => setConfirmationModal(true)}
            sx={{ backgroundColor: theme?.customSettings?.themeColor || '#1976d2', color: '#fff' }}
          >
            Submit
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>BOQ ID</StyledTableCell>
              <StyledTableCell>Sub Category1</StyledTableCell>
              <StyledTableCell>Sub Category2</StyledTableCell>
              <StyledTableCell>Sub Category3</StyledTableCell>
              <StyledTableCell>Rate</StyledTableCell>
              <StyledTableCell>Quantity</StyledTableCell>
              <StyledTableCell>Total Cost</StyledTableCell>
              <StyledTableCell>Edit</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={8}>No data available</StyledTableCell>
              </TableRow>
            ) : (
              filteredData.map((row, index) => (
                <TableRow key={index}>
                  <StyledTableCell>
                    <Chip label={row?.boQ_Alias} size="small" />
                  </StyledTableCell>

                  {["subCategory1", "subCategory2", "subCategory3"].map((field, i) => (
                    <StyledTableCell key={i}>{row[field]}</StyledTableCell>
                  ))}

                  <StyledTableCell>
                    {isRowEditable(index) ? (
                      <TextField
                        size="small"
                        variant="standard"
                        value={row["rate"] || ""}
                        onChange={(e) => handleChange(index, "rate", e.target.value)}
                        sx={{ minWidth: 120 }}
                      />
                    ) : (
                      row["rate"]
                    )}
                  </StyledTableCell>

                  <StyledTableCell>{row["quantity"]}</StyledTableCell>
                  <StyledTableCell>{row["totalCost"]}</StyledTableCell>

                  <StyledTableCell>
                    {isRowEditable(index) ? (
                      <IconButton onClick={() => handleToggleEditClick(index)}>
                          {/* <RxCross2 /> */}
                        <CloseIcon style={{height:"20px", width:"20px"}} />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => handleToggleEditClick(index)}>
                        {/* <MdOutlineModeEdit /> */}
                        <EditIcon style={{width:"20px", height:"20px"}} />
                      </IconButton>
                    )}
                  </StyledTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmationModal} onClose={() => setConfirmationModal(false)}>
        <DialogTitle
          sx={{
            color: "#252525",
            fontSize: "20px",
            lineHeight: "24px",
            fontWeight: 500,
          }}
        >
          Accept Changes
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: "#474747",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 400,
            }}
          >
            Are you sure you want to accept the changes? You can re edit
            this later.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            marginBottom: "20px",
          }}
        >
          <Button
            onClick={() => setConfirmationModal(false)}
            variant="outlined"
            sx={{
              color: theme?.customSettings?.themeColor,
              fontSize: "16px",
              fontWeight: "500",
              textTransform: "none",
              border: `1px solid ${theme?.customSettings?.themeColor}`,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitConfirm}
            variant="contained"
            sx={{
              backgroundColor: theme?.customSettings?.themeColor,
              color: "#fff",
              fontSize: "16px",
              fontWeight: "500",
              textTransform: "none",
              marginRight: "20px",
            }}
          >
            Accept Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllBOQ;
